
import { useStore } from "vuex";
import { computed, ref, watch } from "vue";
import _ from "lodash";
import FullscreenButton from "@/components/FullscreenButton.vue";
import ISO6391 from "iso-639-1";
import {
  HumanSpeechLanguage,
  CallQueuePositionChangedReason,
  DeviceType,
  EndpointStatus,
  MessageType,
  RequestedAction
} from "@moderrotech/servers-communication-library/src/types";
import moment from "moment/moment";
import Timer from "@/components/Timer.vue";
import { getLangName } from "@moderrotech/servers-communication-library/src/helpers/lang";
import { getDeviceType } from "@moderrotech/servers-communication-library/src/helpers/device";
import { $enum } from "ts-enum-util";

export default {
  components: {
    Timer,
    FullscreenButton
  },
  setup() {
    const store = useStore(),
      getProperty = (name: string) => store.getters["auth/property"](name);
    const endpointStatusCode = computed(() => store.getters.endpointStatusCode),
      isVideoOn = computed(() => store.getters.isVideoOn),
      isCameraEnabled = computed(
        () => store.state.session.mediaDevices.isCameraEnabled
      );

    const handleCameraEnableClick = async () =>
        await store.dispatch("session/mediaDevices/enableCamera"),
      handleCameraDisableClick = async () =>
        await store.dispatch("session/mediaDevices/disableCamera");

    const callStartedAt = ref<number | null>(null);

    const visibleSettings = ref<"appLocale" | "sourceLanguage" | null>(null);

    watch(
      () => endpointStatusCode.value === EndpointStatus.connected,
      val => {
        if (val) {
          callStartedAt.value = moment().unix();
        } else {
          callStartedAt.value = null;
          if (visibleSettings.value === "sourceLanguage") {
            visibleSettings.value = null;
          }
        }
      },
      { immediate: true }
    );

    const isTranslatorEnabled = computed(() =>
      getProperty("aws.translator.enabled")
    );

    const sourceLanguage = computed(
      (): HumanSpeechLanguage | null =>
        store.state.session.sourceLanguage ??
        getProperty("aws.translation.source.language") ??
        null
    );

    const supportedLocales = computed((): string[] =>
        _.keys(
          _.pickBy(
            store.state.config.config.appLocalesOrder ?? {},
            v => +v >= 0
          )
        )
      ),
      appLocale = computed(
        (): "en" | "ca" | "es" | "he" | "pt" | "ja" | null =>
          store.state.config.config.appLocale
      );

    watch(
      () => sourceLanguage.value,
      (newVal, oldVal) => {
        // source language has been changed manually (by a client or an expert)
        if (
          oldVal &&
          newVal &&
          supportedLocales.value.includes(_.first(_.split(newVal, "-"))!)
        ) {
          store.commit("config/PATCH_CONFIG", {
            appLocale: _.first(_.split(newVal, "-"))
          });
        }
      },
      { immediate: true }
    );

    watch(
      () => [
        endpointStatusCode.value === EndpointStatus.connected,
        sourceLanguage.value
      ],
      ([v1, v2]) => {
        if (v1 && v2) {
          store.dispatch("session/sendJsonMessage", {
            message: {
              type: MessageType.awsTranslationTargetLanguage,
              languageCode: v2
            }
          });
        }
      },
      { immediate: true }
    );

    const requestedRemoteActions = computed(
      (): { action: RequestedAction; payload?: any }[] | null =>
        store.state.session.requestedActions
    );

    watch(
      () =>
        !!_.find(
          requestedRemoteActions.value,
          v =>
            v.action === RequestedAction.showTranslationSourceLanguageSelector
        ),
      v => {
        if (v) {
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.showTranslationSourceLanguageSelector
          );
          visibleSettings.value = "sourceLanguage";
        }
      },
      { immediate: true }
    );

    watch(
      () =>
        _.find(
          requestedRemoteActions.value,
          v => v.action === RequestedAction.setTranslationSourceLanguage
        ),
      v => {
        if (v) {
          store.commit("session/SET_SOURCE_LANGUAGE", v.payload);
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.setTranslationSourceLanguage
          );
        }
      },
      { immediate: true }
    );

    return {
      isKioskType: computed(() => store.getters["auth/isKioskType"]),
      visibleSettings,
      handleAppLocaleSelect: (locale: string) => {
        store.commit("config/PATCH_CONFIG", { appLocale: locale });
        visibleSettings.value = null;
      },
      appLocales: computed(() => {
        const keys = _.map(
            [..._.keys(Array(supportedLocales.value.length))],
            v =>
              _.findKey(
                store.state.config.config.appLocalesOrder,
                l => l === +v
              )!
          ),
          values = _.map(keys, k => ISO6391.getNativeName(k));

        return _.zipObject(keys, values);
      }),
      appLocale,
      isVideoOn,
      isCameraEnabled,
      handleCameraEnableClick,
      handleCameraDisableClick,
      callStartedAt,
      isTimerVisible: computed(
        () =>
          [EndpointStatus.placingCall, EndpointStatus.connected].includes(
            endpointStatusCode.value
          ) ||
          (store.state.queue.position !== null &&
            store.state.queue.position >= 0 &&
            ![
              CallQueuePositionChangedReason.noExperts,
              CallQueuePositionChangedReason.noTopic,
              CallQueuePositionChangedReason.timeout
            ].includes(store.state.queue.positionChangeReason))
      ),
      isFullscreenButtonVisible: !_.includes(
        [DeviceType.mobile, DeviceType.tablet],
        getDeviceType()
      ),
      isTranslatorEnabled,
      sourceLanguage,
      handleSourceLanguageChange: (l: HumanSpeechLanguage) => {
        store.commit("session/SET_SOURCE_LANGUAGE", l);
        visibleSettings.value = null;
      },
      endpointStatusCode,
      EndpointStatus,
      getLangName,
      HumanSpeechLanguage,
      ISO6391,
      $enum,
      ..._.pick(_, ["toUpper", "first", "split", "keys"])
    };
  }
};
